import { isRef, onBeforeUnmount, ref, watch } from 'vue';
import { useAuthStore } from '@/stores/useAuthStore';
import { useUserStore } from '@/stores/useUserStore';
import { TOOL_DRAFT_PREFIX } from '@/storage/keys';

export function openDemoPaywall(event) {
  if (event?.type === 'keydown' && ['Tab', 'Shift', 'Control', 'Alt', 'Meta', 'Escape'].includes(event.key)) return;
  event?.preventDefault();
  event?.stopImmediatePropagation();
  window.dispatchEvent(new CustomEvent('entitlement:missing', { detail: { demo: true } }));
}

const snapshot = (fields) =>
  Object.fromEntries(Object.entries(fields).map(([name, source]) => [name, isRef(source) ? source.value : source]));

/** Write a stored snapshot back into the fields; true when there was one. */
function applySaved(fields, saved) {
  if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return false;
  Object.entries(fields).forEach(([name, target]) => {
    if (!(name in saved)) return;
    if (isRef(target)) target.value = target.value instanceof Date ? new Date(saved[name]) : saved[name];
    else if (saved[name] && typeof saved[name] === 'object') Object.assign(target, saved[name]);
  });
  return true;
}

/** Tab-scoped input only: no account data, payment authorization or automatic submission. */
export function useDemoDraft(tool, fields, { enabled = true } = {}) {
  const auth = useAuthStore();
  const key = `pf_demo_draft:${tool}`;
  let active = false;
  let retained = false;
  const save = () => {
    if (!enabled || !active || (auth.isAuthenticated && !retained)) return;
    try {
      sessionStorage.setItem(key, JSON.stringify(snapshot(fields)));
    } catch { /* A blocked storage area must not break the form. */ }
  };
  /** Restore a guest's draft; true when one came back. */
  const restore = () => {
    if (!enabled) return false;
    try {
      retained = applySaved(fields, JSON.parse(sessionStorage.getItem(key) || 'null'));
    } catch { sessionStorage.removeItem(key); }
    active = true;
    return retained;
  };
  watch(() => Object.values(fields).map(source => isRef(source) ? source.value : source), save, { deep: true });
  onBeforeUnmount(save);
  const clear = () => { active = false; retained = false; sessionStorage.removeItem(key); };
  return { restore, clear };
}

/**
 * A signed-in user's unsent form, kept on this device until it is submitted or
 * discarded (PI-713). Saving starts at restore(), so loading the page stores nothing,
 * and an untouched form stores nothing either. `pf_` keys are wiped on logout.
 */
export function useSavedDraft(tool, fields, { enabled = true } = {}) {
  const user = useUserStore();
  const restored = ref(false);
  let active = false;
  let pristine = '';
  const key = () => (user.id ? `${TOOL_DRAFT_PREFIX}${encodeURIComponent(user.id)}:${tool}` : '');
  const save = () => {
    const current = key();
    if (!enabled || !active || !current) return;
    const json = JSON.stringify(snapshot(fields));
    try {
      if (json === pristine) localStorage.removeItem(current);
      else localStorage.setItem(current, json);
    } catch { /* A blocked storage area must not break the form. */ }
  };
  const restore = () => {
    const current = key();
    if (!enabled || !current) return;
    try {
      restored.value = applySaved(fields, JSON.parse(localStorage.getItem(current) || 'null'));
    } catch { localStorage.removeItem(current); }
    pristine = restored.value ? '' : JSON.stringify(snapshot(fields));
    active = true;
  };
  /** Drop the draft and start over from `reset`, still saving what comes next. */
  const discard = (reset) => {
    restored.value = false;
    try { localStorage.removeItem(key()); } catch { /* nothing stored to drop */ }
    reset();
    pristine = JSON.stringify(snapshot(fields));
  };
  /** Submitted: the draft became a run. */
  const clear = () => {
    active = false;
    restored.value = false;
    try { localStorage.removeItem(key()); } catch { /* nothing stored to drop */ }
  };
  watch(() => JSON.stringify(snapshot(fields)), save);
  onBeforeUnmount(save);
  return { restore, discard, clear, restored };
}
